import { useSearchParams } from "react-router-dom";
import { DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme } from "next-themes";
import { useMealsDashboard } from "../../hooks/useApi";
import {
  Utensils,
  TrendingUp,
  Camera,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from "recharts";

const COLORS = [
  "hsl(var(--chart-2))",
  "hsl(var(--primary))",
  "hsl(var(--chart-3))",
  "hsl(var(--chart-4))",
];

const tooltipStyle = {
  backgroundColor: "hsl(var(--card))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  color: "hsl(var(--foreground))",
};

// Icon mapping
const iconMap: Record<string, any> = {
  Utensils,
  TrendingUp,
  Camera,
  Clock,
};

const AdminMeals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  // Always read date from URL as source of truth, default to today if not present
  const urlDate = searchParams.get('date');
  const selectedDate = urlDate ? dayjs(urlDate) : dayjs();

  // Handle date change from DatePicker
  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      setSearchParams({ date: date.format("YYYY-MM-DD") });
    } else {
      setSearchParams({ date: dayjs().format("YYYY-MM-DD") });
    }
  };

  const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined;

  // Fetch meals dashboard data
  const { data: mealsDashboard, isLoading } = useMealsDashboard(dateParam);

  // Transform API data
  // Icon name mapping (API sends "utensils", "trending-up", "camera" etc.)
  const iconKeyMap: Record<string, any> = {
    "utensils": Utensils,
    "trending-up": TrendingUp,
    "camera": Camera,
    "clock": Clock,
    Utensils, TrendingUp, Camera, Clock,
  };

  const mealStats = (mealsDashboard?.metrics ?? []).map(metric => ({
    ...metric,
    title: metric.label,
    icon: iconKeyMap[metric.icon] || Utensils,
  }));

  const mealsPerDay = mealsDashboard?.meals_logged_per_day ?? [];
  const mealTypeDistribution = mealsDashboard?.meal_type_split ?? [];
  const bbsDistribution = mealsDashboard?.bbs_distribution ?? [];
  const popularFoods = mealsDashboard?.most_logged_foods ?? [];
  // Calculate max from actual data to ensure proper proportional bars
  const mostLoggedFoodsMax = popularFoods.length > 0 
    ? Math.max(...popularFoods.map(f => f.logs)) 
    : 1;
  const scanAccuracy = mealsDashboard?.ai_scan_accuracy ?? [];
  const nutritionTrends = mealsDashboard?.avg_nutrition_trends ?? [];
  const glucoseSpikeByMeal = (mealsDashboard?.avg_glucose_spike_by_meal ?? []).map(d => ({
    meal: d.name,
    avgSpike: d.avg_mg_dl,
    count: d.count,
  }));
  const glucoseSpikeTrendByMeal = mealsDashboard?.glucose_spike_trend_by_meal ?? [];

  return (
    <div className="space-y-6">
      {/* Page Header with Date Picker */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Meals & Glucose Analytics
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Meal logging patterns and per-meal glucose spike predictions
          </p>
        </div>
        <ConfigProvider
          theme={{
            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
            token: {
              colorPrimary: isDark ? "hsl(221, 83%, 53%)" : "hsl(221, 65%, 58%)",
              colorBgContainer: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorBgElevated: isDark ? "hsl(224, 71%, 4%)" : "hsl(0, 0%, 100%)",
              colorText: isDark ? "hsl(210, 40%, 98%)" : "hsl(240, 10%, 10%)",
              colorTextPlaceholder: isDark ? "hsl(215, 20%, 65%)" : "hsl(240, 4%, 46%)",
              colorBorder: isDark ? "hsl(224, 71%, 15%)" : "hsl(240, 6%, 90%)",
              borderRadius: 8,
            },
          }}
        >
          <DatePicker
            format="MMM D, YYYY"
            value={selectedDate}
            onChange={handleDateChange}
            allowClear
          />
        </ConfigProvider>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <p className="text-muted-foreground">Loading meals data...</p>
        </div>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mealStats.length === 0 ? (
              <p className="col-span-3 text-center text-muted-foreground">No meal stats available</p>
            ) : (
              mealStats.map(stat => (
                <Card key={stat.title}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
                        <stat.icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">{stat.title}</p>
                        <div className="flex items-center gap-2">
                          <p className="text-xl font-bold text-foreground">
                            {stat.value}
                          </p>
                          <span className={`flex items-center text-xs font-semibold ${stat.changeType === 'positive' ? 'text-emerald-600' : 'text-red-500'}`}>
                            {stat.changeType === 'positive'
                              ? <ArrowUpRight className="h-3 w-3" />
                              : <ArrowDownRight className="h-3 w-3" />}
                            {stat.change}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Meals Per Day + Distribution */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Meals Logged Per Day</CardTitle>
                <CardDescription>Breakdown by meal type this week</CardDescription>
              </CardHeader>
              <CardContent>
                {mealsPerDay.length === 0 ? (
                  <div className="flex h-64 items-center justify-center text-muted-foreground">
                    No data available
                  </div>
                ) : (
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={mealsPerDay}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          className="stroke-muted"
                        />
                        <XAxis
                          dataKey="day"
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 12,
                          }}
                        />
                        <YAxis
                          tick={{
                            fill: "hsl(var(--muted-foreground))",
                            fontSize: 12,
                    }}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Legend />
                  <Bar
                    dataKey="Breakfast"
                    name="Breakfast"
                    fill="hsl(var(--chart-2))"
                    radius={[2, 2, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="Lunch"
                    name="Lunch"
                    fill="hsl(var(--primary))"
                    radius={[2, 2, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="Dinner"
                    name="Dinner"
                    fill="hsl(var(--chart-3))"
                    radius={[2, 2, 0, 0]}
                    stackId="a"
                  />
                  <Bar
                    dataKey="Snacks"
                    name="Snacks"
                    fill="hsl(var(--chart-4))"
                    radius={[2, 2, 0, 0]}
                    stackId="a"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Meal Type Split</CardTitle>
            <CardDescription>Overall distribution</CardDescription>
          </CardHeader>
          <CardContent>
            {mealTypeDistribution.length === 0 ? (
              <div className="flex h-44 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
                      <Pie
                        data={mealTypeDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={45}
                        outerRadius={70}
                        paddingAngle={4}
                        dataKey="value"
                        label={(entry) =>
                          entry.value > 0 ? `${entry.name}: ${entry.value}%` : ''
                        }
                        labelLine={(entry) => entry.value > 0}
                      >
                        {mealTypeDistribution.map((_, i) => (
                          <Cell key={i} fill={COLORS[i]} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-2 space-y-1.5">
                  {mealTypeDistribution.filter((item) => item.value > 0).map((item, i) => {
                    const colorIndex = mealTypeDistribution.indexOf(item);
                    return (
                      <div
                        key={item.name}
                        className="flex items-center gap-2 text-xs"
                      >
                        <div
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: COLORS[colorIndex] }}
                        />
                        <span className="text-muted-foreground">{item.name}</span>
                        <span className="ml-auto font-semibold text-foreground">
                          {item.value}%
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Nutrition Trends + Gut Score Distribution */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Avg Nutrition Trends</CardTitle>
            <CardDescription>Weekly averages across all users</CardDescription>
          </CardHeader>
          <CardContent>
            {nutritionTrends.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={nutritionTrends}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="week"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Protein"
                      name="Protein (g)"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="Carbs"
                      name="Carbs (g)"
                      stroke="hsl(var(--chart-2))"
                      fill="hsl(var(--chart-2))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                    <Area
                      type="monotone"
                      dataKey="Fats"
                      name="Fat (g)"
                      stroke="hsl(var(--chart-3))"
                      fill="hsl(var(--chart-3))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Camera Scan Accuracy
            </CardTitle>
            <CardDescription>
              Food identification accuracy rates
            </CardDescription>
          </CardHeader>
          <CardContent>
            {scanAccuracy.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <>
                <div className="space-y-3">
                  {scanAccuracy.map((item) => (
                    <div key={item.category}>
                      <div className="mb-1.5 flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">{item.category}</span>
                        <span className="font-semibold text-foreground">
                          {item.value}% ({item.count} scans)
                        </span>
                      </div>
                      <Progress value={item.value} />
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-lg bg-muted/50 p-3">
                  <p className="text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">
                      {mealsDashboard?.ai_scan_accuracy_items ?? 0} total scans
                    </span>{" "}
                    — food identification accuracy breakdown
                  </p>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* BBS Distribution + Popular Foods */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">BBS Score Distribution</CardTitle>
            <CardDescription>Users by Bite Balance Score range</CardDescription>
          </CardHeader>
          <CardContent>
            {bbsDistribution.length === 0 ? (
              <div className="flex h-56 items-center justify-center text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={bbsDistribution}
                    layout="vertical"
                    margin={{ top: 4, right: 20, left: 10, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" horizontal={false} />
                    <XAxis
                      type="number"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      allowDecimals={false}
                    />
                    <YAxis
                      type="category"
                      dataKey="range"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      width={70}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: number) => [v, "Users"]}
                    />
                    <Bar dataKey="users" name="Users" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Most Logged Foods</CardTitle>
            <CardDescription>Top food items across all users</CardDescription>
          </CardHeader>
          <CardContent>
            {popularFoods.length === 0 ? (
              <div className="flex items-center justify-center py-8 text-muted-foreground">
                No data available
              </div>
            ) : (
              <div className="space-y-3">
                {popularFoods.map((food, i) => {
                  const progressValue = (food.logs / mostLoggedFoodsMax) * 100;
                  return (
                    <div key={food.name} className="flex items-center gap-3">
                      <span className="w-5 text-xs font-bold text-muted-foreground">
                        #{i + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 flex items-center justify-between">
                          <p className="text-sm font-medium text-foreground">
                            {food.name}
                          </p>
                          <span className="text-xs text-muted-foreground">
                            {food.logs.toLocaleString()} logs
                          </span>
                        </div>
                        <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                          <div 
                            className="h-full bg-primary transition-all duration-300"
                            style={{ width: `${progressValue}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Per-Meal Glucose Spike Analytics */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-amber-500" />
            Avg Predicted Glucose Spike by Meal
          </CardTitle>
          <CardDescription>
            Average spike (mg/dL) per meal type across all users
          </CardDescription>
        </CardHeader>
        <CardContent>
          {glucoseSpikeByMeal.length === 0 ? (
            <div className="flex h-80 items-center justify-center text-muted-foreground">
              No data available
            </div>
          ) : (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={glucoseSpikeByMeal} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    className="stroke-muted"
                  />
                  <XAxis
                    dataKey="meal"
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 13,
                    }}
                  />
                  <YAxis
                    tick={{
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 12,
                    }}
                    label={{
                      value: "mg/dL",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      style: { fill: "hsl(var(--muted-foreground))", fontSize: 12 },
                    }}
                    width={50}
                  />
                  <Tooltip
                    contentStyle={tooltipStyle}
                    formatter={(v: number) => [`${v} mg/dL`, "Avg Spike"]}
                  />
                  <Bar
                    dataKey="avgSpike"
                    name="Avg Spike"
                    fill="hsl(var(--chart-4))"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Glucose Spike Trend by Meal Type */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-4 w-4 text-amber-500" />
            Glucose Spike Trend by Meal Type
          </CardTitle>
          <CardDescription>
            Weekly avg predicted spike (mg/dL) per meal
          </CardDescription>
        </CardHeader>
        <CardContent>
          {glucoseSpikeTrendByMeal.length === 0 ? (
            <div className="flex h-96 items-center justify-center text-muted-foreground">
              No data available
            </div>
          ) : (
            <>
              <div className="h-96 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={glucoseSpikeTrendByMeal} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      className="stroke-muted"
                    />
                    <XAxis
                      dataKey="week"
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 13,
                      }}
                    />
                    <YAxis
                      tick={{
                        fill: "hsl(var(--muted-foreground))",
                        fontSize: 12,
                      }}
                      label={{
                        value: "mg/dL",
                        angle: -90,
                        position: "insideLeft",
                        offset: 10,
                        style: { fill: "hsl(var(--muted-foreground))", fontSize: 12 },
                      }}
                      width={50}
                    />
                    <Tooltip
                      contentStyle={tooltipStyle}
                      formatter={(v: number, name: string) => [`${v} mg/dL`, name]}
                    />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Breakfast"
                      name="Breakfast"
                      stroke="hsl(var(--chart-2))"
                      fill="hsl(var(--chart-2))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="Lunch"
                      name="Lunch"
                      stroke="hsl(var(--primary))"
                      fill="hsl(var(--primary))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="Dinner"
                      name="Dinner"
                      stroke="hsl(var(--chart-3))"
                      fill="hsl(var(--chart-3))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="Snacks"
                      name="Snacks"
                      stroke="hsl(var(--chart-4))"
                      fill="hsl(var(--chart-4))"
                      fillOpacity={0.1}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </>
          )}
        </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default AdminMeals;
